import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import type { SidebarRow } from '@/components/sidebar'
import { CacheKeys } from '../lib/cache'
import { dropZeroShares } from '../lib/freightAllocation'
import { CamelToSnakeCaseNested, PaginatedResults, toCamelCase, toSnakeCase } from '../lib/typeHelper'
import { Business, type OrgBusiness } from './business'
import type { FreightSuggestions } from './fillApplication'
import { InsuranceApplication } from './insuranceApplication'
import type { CoverageType } from './insuranceCoverage'
import type { InsurancePolicy } from './insurancePolicy'
import type { MaintenanceNotice } from './maintenance'
export type { Document, DocumentAttachment } from './document'
export { documentTypeLabels, useOrgDocuments } from './document'

export enum OrgStatus {
  ONBOARDING = 'ONBOARDING',
  /** Carriers have been asked for prices — the customer is picking a quote. */
  QUOTING = 'QUOTING',
  UNDERWRITING = 'UNDERWRITING',
  ACTIVE = 'ACTIVE',
}

export enum OrgRole {
  BUSINESS_OWNER = 'BUSINESS_OWNER',
  ADMIN = 'ADMIN',
  USER = 'USER',
}

export function getOrgRoleLabel(role: OrgRole): string {
  return role === OrgRole.ADMIN ? 'Admin' : role === OrgRole.BUSINESS_OWNER ? 'Business Owner' : 'User'
}

/** False for an account with no org of its own, such as some staff accounts. */
export function hasOrg(user: User): boolean {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- the type assumes an org, but the API can send none
  return user.currentOrg != null
}

export function isOrgAdmin(user: User): boolean {
  return user.orgUser.role === OrgRole.ADMIN || user.orgUser.role === OrgRole.BUSINESS_OWNER
}

/** Still choosing insurance — filling in the application, or comparing quotes. */
export function isOrgShopping(org: Org | undefined | null): boolean {
  return org?.status === OrgStatus.ONBOARDING || org?.status === OrgStatus.QUOTING
}

/** Carriers have been asked for prices, so quotes are what this org has to look at. */
export function isOrgQuoting(org: Org | undefined | null): boolean {
  return org?.status === OrgStatus.QUOTING
}

/** Quotes are what this org has to look at — comparing them, or waiting on an underwriter. */
export function isOrgReviewingQuotes(org: Org | undefined | null): boolean {
  return isOrgQuoting(org) || org?.status === OrgStatus.UNDERWRITING
}

/**
 * Whether signing in lands on the application — its chat, or /quotes once carriers have
 * been asked. Every pre-coverage status does — filling the application in, comparing
 * quotes, or waiting on an underwriter — since that is where the outstanding work is,
 * whichever of the three they're on.
 */
export function landsOnApplication(org: Org | undefined | null): boolean {
  return isOrgShopping(org) || isOrgReviewingQuotes(org)
}

/**
 * Whether the customer belongs on /quotes rather than the /application chat. The
 * routes and the banner pointing at them all read this, so the nudge can't promise
 * quotes the destination won't show.
 */
export function showsQuotesPage(
  org: Org | undefined | null,
  application: Pick<InsuranceApplicationSummary, 'hasDispatchedIndications'> | null | undefined
): boolean {
  // `??`, not `||`: a stale QUOTING status must not override an application that says no.
  return application?.hasDispatchedIndications ?? isOrgQuoting(org)
}

/** Coverage in force today — the same signal the backend's pending-change gate reads. */
export function hasPolicyInForce(org: Org | undefined | null): boolean {
  return (org?.currentPolicies ?? []).length > 0
}

/** Ever bound a policy, expired ones included: a claim can come from a term that has ended. */
export function hasHadCoverage(org: Org | undefined | null): boolean {
  return (org?.insurancePolicies ?? []).length > 0
}

/** The status a staff pill shows: an ACTIVE customer whose policies have all ended reads Inactive. */
export function orgPillStatus(org: Pick<Org, 'status' | 'isLapsed'>): string {
  return org.isLapsed ? 'inactive' : org.status
}

/** An active fleet — coverage in force, whatever a renewal-era status says. */
export function isActiveFleet(org: Org | undefined | null): boolean {
  return org?.status === OrgStatus.ACTIVE || hasPolicyInForce(org)
}

/**
 * Quotes are this fleet's open business and nothing is bound yet, so its uploads and edits
 * go to the quotes page and Ask Cai stays shut — customers were bargaining over prices in
 * the chat. A covered fleet shopping a renewal keeps both, since its policy needs servicing.
 */
export function worksFromQuotesPage(org: Org | undefined | null): boolean {
  return isOrgReviewingQuotes(org) && !hasPolicyInForce(org)
}

/**
 * The app-wide nudge runs until the fleet is covered — and only for the org admins
 * /application lets through, since it bounces everyone else to Insurance.
 */
export function showsOnboardingBanner(user: User | undefined | null): boolean {
  if (!user || !isOrgAdmin(user)) return false
  if (isActiveFleet(user.currentOrg)) return false
  return landsOnApplication(user.currentOrg)
}

/** Which nudge the app-wide banner shows. */
export enum OnboardingStage {
  APPLICATION_INCOMPLETE = 'APPLICATION_INCOMPLETE',
  QUOTES_READY = 'QUOTES_READY',
  /** Checkout requested — the broker has it and the customer owes nothing. */
  WITH_BROKER = 'WITH_BROKER',
}

/**
 * Read the banner's stage off the same signals its CTA depends on. An org that
 * has no quotes page to land on falls back to the application nudge whatever its
 * status says, so the button can't promise quotes the destination won't show.
 */
export function onboardingStage(
  org: Org | undefined | null,
  application:
    Pick<InsuranceApplicationSummary, 'hasDispatchedIndications' | 'hasAppliedIndications'> | null | undefined
): OnboardingStage {
  if (!showsQuotesPage(org, application)) {
    return OnboardingStage.APPLICATION_INCOMPLETE
  }
  // Not org.status: staff can move an org to UNDERWRITING with nothing checked out.
  return application?.hasAppliedIndications ? OnboardingStage.WITH_BROKER : OnboardingStage.QUOTES_READY
}

/** The banner is asking the customer to finish their application, where the roster is attested. Staff are exempt. */
export function continuesApplication(user: User | undefined): boolean {
  if (!user || user.isCaiAdmin || !showsOnboardingBanner(user)) return false
  const stage = onboardingStage(user.currentOrg, user.currentOrg.currentInsuranceApplication)
  return stage === OnboardingStage.APPLICATION_INCOMPLETE
}

/** Fleet data is editable only while the org is shopping — underwriting and active lock it, Cai staff aside. */
export function isFleetReadonly(user: User | undefined): boolean {
  return !user?.isCaiAdmin && !isOrgShopping(user?.currentOrg)
}

/** What a roster page lets this user do with trucks, trailers and drivers. */
export enum FleetEditMode {
  /** Writes land on the live row straight away. */
  EDITABLE = 'EDITABLE',
  /** Writes are accepted but enter Cai's queue as pending requests. */
  REVIEWED = 'REVIEWED',
  /** No writes at all. */
  LOCKED = 'LOCKED',
}

export function fleetEditMode(user: User | undefined): FleetEditMode {
  if (user?.isCaiAdmin) return FleetEditMode.EDITABLE
  // Coverage outranks status: a renewing org shops at QUOTING with a policy the gate still catches.
  if (hasPolicyInForce(user?.currentOrg)) return FleetEditMode.REVIEWED
  // A fleet between terms has no gate to divert the write, so it stays shut.
  return isOrgShopping(user?.currentOrg) ? FleetEditMode.EDITABLE : FleetEditMode.LOCKED
}

/**
 * Whether a roster page hands its edits to the quotes page instead of taking them.
 *
 * Quoting only. The destination has to render *and* accept the edit: underwriting makes
 * the quotes page read-only and hands the roster to the broker, so a fleet there keeps
 * the underwriting banner rather than a promise the page cannot honour.
 */
export function showsQuotesLock(
  user: User | undefined,
  application: Pick<InsuranceApplicationSummary, 'hasDispatchedIndications'> | null | undefined
): boolean {
  if (user?.isCaiAdmin || hasPolicyInForce(user?.currentOrg)) return false
  return isOrgQuoting(user?.currentOrg) && showsQuotesPage(user?.currentOrg, application)
}

/** What a roster page lets this user do, once the quotes-page hand-off has been ruled out. */
export function rosterEditMode(user: User | undefined, quotesLocked: boolean): FleetEditMode {
  return quotesLocked ? FleetEditMode.LOCKED : fleetEditMode(user)
}

// Same as User but slim to list all users in org
/** Whether the member proved their address, with a code or Google sign-in. */
export enum EmailVerification {
  VERIFIED = 'VERIFIED',
  UNVERIFIED = 'UNVERIFIED',
}

export interface OrgMember {
  id: string
  customUserId: string | null
  email: string
  mobile: string | null
  firstName: string
  middleName: string | null
  lastName: string
  dob: string | null
  dateCreated: string
  dateUpdated: string
  orgUser: OrgUser
  emailVerification: EmailVerification
}

export interface TravelDistanceItem {
  value: string
  label: string
  emoji: string
}

export interface CommodityItem {
  name: string
  percentage: number
}

export interface BusinessOwner {
  id: string
  firstName: string
  lastName: string
  email: string
  dob: string | null
  mobile: string | null
  maritalStatus: string | null
  /** The driver designated as this owner, if any — info is synced from that driver. */
  driverId: string | null
}

export interface ConfirmInformationCard {
  key: string
  title: string
  valid: boolean
  /** Required fields left blank — rendered as "Add …". */
  missing: string[]
  /** A value no carrier will take — rendered as "Fix …", and it gates the step.
   *  Optional because a backend predating this field returns no key at all (deploy skew). */
  invalid?: string[]
  /** Shown on the card and never gated — a worse price, not a dead quote. */
  warnings?: string[]
}

/** One parsed loss run row — a single coverage line over a single policy period. */
export interface LossRunEntry {
  insurer: string
  periodStart: string
  periodEnd: string
  /** Number of claims in this period, as typed into the form. Blank when the report didn't state one. */
  claimCount: string
  totalLosses: string
  reserve: string
  incidentDate: string
}

export interface LossRunSummary {
  defaultValues: Record<'auto_liability' | 'physical_damage' | 'cargo' | 'general_liability', LossRunEntry[]>
  isParsing: boolean
  hasReportOnFile: boolean
}

export interface InsuranceApplicationSummary {
  id: string
  /** ISO date the customer wants coverage to start; null until they answer. */
  desiredEffectiveDate?: string | null
  selectedCoverages?: CoverageType[]
  travelDistance: TravelDistanceItem[] | null
  travelDistanceOptions: TravelDistanceItem[]
  commodities: CommodityItem[]
  businessOwner: BusinessOwner | null
  confirmInformationValidation?: ConfirmInformationCard[]
  entityType?: string | null
  hasEld?: boolean | null
  eldProvider?: string | null
  coverageLapseDetected?: boolean
  cancellationReason?: string | null
  lossRunSummary?: LossRunSummary
  /** Optional because a backend predating this field returns no key at all (deploy skew). */
  hasDispatchedIndications?: boolean
  /** The customer checked out and their picks are with the broker. Optional for the same reason. */
  hasAppliedIndications?: boolean
  /** Staff only: when the application first changed after carriers last priced it. */
  dateEditedSinceQuote?: string | null
  /** Staff only: carriers with a card that priced an older version of the application. */
  outOfDateVendors?: string[]
  /** Staff only: what changed since the last full quote. Some edits save no row. */
  requoteChanges?: RequoteChange[]
}

export interface RequoteChange {
  label: string
  before: string
  after: string
  /** Both values are known, so the row reads "before → after". */
  moved: boolean
}

export interface SalesOwner {
  id: string
  email: string
  firstName: string
  lastName: string
  /** Their headshot (else their legacy avatar); '' when they have neither. */
  avatar: string
}

export interface Org {
  id: string
  dateCreated: string
  dateUpdated: string
  name: string
  status: OrgStatus
  /** ACTIVE, but every policy has ended or been canceled. Optional for deploy skew. */
  isLapsed?: boolean
  demoMode: boolean
  /** Submitted a large-fleet packet directly — brokers should not prompt them to complete the standard application. */
  isLargeFleet: boolean
  business: OrgBusiness | null
  users: OrgMember[]
  pendingInvitations: OrgPendingInvitation[]
  insurancePolicies: InsurancePolicy[]
  /**
   * Every policy in force today — an org can hold one per coverage line.
   * Optional because a backend predating this field returns no key at all
   * (deploy skew), so every read must fall back to [].
   */
  currentPolicies?: InsurancePolicy[]
  currentInsuranceApplication: InsuranceApplicationSummary | null
  salesOwner: SalesOwner | null
  sourceLeadId: string | null
  coiManualGeneration: boolean
  /**
   * Whether any policy is manually issued — true whenever coiManualGeneration
   * is, and also for an org running both modes at once. Optional because a
   * backend predating this field returns no key at all (deploy skew).
   */
  coiHasManualPolicy?: boolean
  /**
   * Whether an ELD is wired up through Terminal. Optional because a backend
   * predating this field returns no key at all (deploy skew).
   */
  eld?: EldConnection
}

/** An org's ELD link, as Terminal reports it. */
export interface EldConnection {
  connected: boolean
  /** The telematics vendor, e.g. "Samsara" — blank when Terminal didn't name one. */
  providerName: string
  /** The vendor's square brand mark on Terminal's CDN; blank when we have no provider code. */
  providerIconUrl: string
  /** Terminal's dashboard page for the connection; blank for providers we don't broker. */
  dashboardUrl: string
}

/**
 * One row of the customers list. The list endpoint deliberately returns far
 * less than `Org` — building the rest costs hundreds of queries per page.
 */
export interface OrgListItem {
  id: string
  dateCreated: string
  dateUpdated: string
  name: string
  status: OrgStatus
  /** ACTIVE, but every policy has ended or been canceled. Optional for deploy skew. */
  isLapsed?: boolean
  demoMode: boolean
  business: Pick<Business, 'id' | 'name' | 'dotNumber' | 'mcNumber'> | null
  /**
   * Optional because a backend predating this field returns no key at all
   * (deploy skew), so every read must fall back to 0.
   */
  userCount?: number
  salesOwner: SalesOwner | null
  /**
   * The policies in force and any renewal already bound, earliest start first.
   * Optional because a backend predating this field returns no key at all
   * (deploy skew), so every read must fall back to [].
   */
  policies?: OrgListPolicy[]
}

/** A policy as a customers-list row shows it. */
export interface OrgListPolicy {
  id: string
  /** The carrier that writes the risk; null when the policy names none. */
  vendorName: string | null
  coverageTypes: CoverageType[]
  /** The day the policy was added to Cai. */
  boundDate: string
  dateStart: string | null
  dateEnd: string | null
}

/** The customers list's sortable columns, as its ordering param names them; "-" in front reverses one. */
export enum CustomerSortColumn {
  NAME = 'name',
  STATUS = 'status',
  USERS = 'users',
  BOUND = 'bound',
  STARTS = 'starts',
  EXPIRES = 'expires',
}

export interface OrgPendingInvitation {
  id: string
  email: string
  role: OrgRole
  dateCreated: string
  dateUpdated: string
}

export enum OrgUserStatus {
  ACTIVE = 'ACTIVE',
  DEACTIVATED = 'DEACTIVATED',
}

export interface OrgUser {
  id: string
  dateCreated: string
  dateUpdated: string
  role: OrgRole
  status: OrgUserStatus
}

export interface PendingInvitation {
  id: string
  orgName: string
  role: string
}

/** One of a user's org memberships — a user can belong to several orgs. */
export interface OrgMembership {
  id: string
  orgId: string
  orgName: string
  orgStatus: OrgStatus
  /** Users recognise their org by the carrier name; null before a carrier exists. */
  businessName: string | null
  role: OrgRole
  status: OrgUserStatus
}

/** What to call an org in the UI — the carrier name, falling back to the org name. */
export function orgMembershipLabel(membership: OrgMembership): string {
  return membership.businessName || membership.orgName
}

export interface User {
  customUserId?: string
  id: string
  email: string
  mobile: string
  dob: string
  firstName: string
  middleName?: string
  lastName: string
  dateCreated: string
  dateUpdated: string
  currentOrg: Org
  orgUser: OrgUser
  /**
   * Every org the user can switch into. Optional because a backend predating
   * this field returns no key at all (deploy skew), so reads must fall back to [].
   */
  organisations?: OrgMembership[]
  isCaiAdmin: boolean
  isStaff: boolean
  /** Side-nav rows, decided by the backend. An empty product is one to pitch, not list. */
  sidebar: SidebarNav
  hubPreferences: HubUserPreferences
  pendingInvitation: PendingInvitation | null
  flags: Record<string, boolean>
  /** Global maintenance notice from /api/me, null when no switch is on. */
  maintenanceBanner: MaintenanceNotice | null
  googleMailConnected: boolean
  googleMailEmail: string | null
  displayLabel: string
  /** Public URL of the uploaded headshot, or null when none is set. */
  photo: string | null
  /** The staff member's own Langfuse user page, every trace of theirs in this environment; null for anyone else. */
  tracesUrl: string | null
}

export type SidebarNavItem = SidebarRow

export interface SidebarNav {
  insurance: SidebarNavItem[]
  vault: SidebarNavItem[]
  /** Owned by the bookkeeper app; empty until we fetch it from there. */
  finance: SidebarNavItem[]
}

export interface HubUserPreferences {
  emailSignature: string
  leadEmailSubject: string
  leadEmailBody: string
}

export interface StaffUser {
  id: string
  email: string
  firstName: string
  lastName: string
  isStaff: boolean
  isCaiAdmin: boolean
  receivesLeadAssignments: boolean
  /** Their headshot (else their legacy avatar); '' when they have neither. */
  avatar: string
}

/** "Daniel Husar", falling back to the email for a user with no name set. */
export function staffUserName(user: Pick<StaffUser, 'firstName' | 'lastName' | 'email'>): string {
  return `${user.firstName} ${user.lastName}`.trim() || user.email
}

export function getCurrentUser() {
  return axios.get('/api/v1/insure/users/me/').then((response) => toCamelCase(response.data) as User)
}

export function useCurrentUser({
  enabled = true,
  staleTime,
}: {
  enabled: boolean
  /** How long a fetched user counts as fresh, so a mount right after a prefetch doesn't refetch. */
  staleTime?: number
}) {
  const {
    data: user,
    isLoading,
    isError,
    error,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: [CacheKeys.CURRENT_USER],
    queryFn: getCurrentUser,
    enabled,
    staleTime,
    refetchInterval: (query) => {
      const user = query.state.data
      return user && hasOrg(user) && user.currentOrg.currentInsuranceApplication?.lossRunSummary?.isParsing
        ? 10_000
        : false
    },
  })
  return { user, isLoading, isError, error, isRefetching, refetch }
}

export function updateUser(id: string, data: Partial<CamelToSnakeCaseNested<User>>) {
  return axios
    .patch(`/api/v1/insure/users/${id}/`, data)
    .then((response) => toCamelCase(response.data) as User)
}

export function useUpdateHubPreferences() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<HubUserPreferences>) =>
      axios
        .patch('/api/v1/insure/users/me/hub-preferences/', toSnakeCase(data))
        .then((response) => toCamelCase(response.data) as HubUserPreferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.CURRENT_USER] })
    },
  })
}

export function useUploadPhoto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData()
      formData.append('photo', file)
      return axios
        .put('/api/v1/insure/users/me/photo/', formData)
        .then((response) => toCamelCase(response.data) as { photo: string | null })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.CURRENT_USER] })
    },
  })
}

export function useDeletePhoto() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => axios.delete('/api/v1/insure/users/me/photo/').then(() => undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.CURRENT_USER] })
    },
  })
}

export function useDisconnectGoogle() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => axios.post('/api/v1/insure/google/oauth/disconnect').then(() => undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.CURRENT_USER] })
    },
  })
}

export interface UpdateBusinessOwnerData {
  firstName?: string
  lastName?: string
  email?: string
  dob?: string | null
  mobile?: string | null
  maritalStatus?: string | null
}

export const MARITAL_STATUS_OPTIONS = [
  { label: 'Single', value: 'SINGLE' },
  { label: 'Married', value: 'MARRIED' },
  { label: 'Divorced', value: 'DIVORCED' },
  { label: 'Widowed', value: 'WIDOWED' },
  { label: 'Separated', value: 'SEPARATED' },
] as const

export function maritalStatusLabel(value: string | null | undefined): string {
  return MARITAL_STATUS_OPTIONS.find((o) => o.value === value)?.label ?? '-'
}

export function updateBusinessOwner(orgId: string, data: UpdateBusinessOwnerData) {
  return axios.patch(`/api/v1/insure/organisations/${orgId}/business-owner/`, toSnakeCase(data))
}

/** Both fields are optional server-side: an omitted one is left as it is on file. */
export function updateCoverageNeeds(
  orgId: string,
  data: { desiredEffectiveDate?: string; selectedCoverages?: CoverageType[] }
) {
  return axios.patch(`/api/v1/insure/organisations/${orgId}/coverage-needs/`, toSnakeCase(data))
}

export function updateTravelDistance(orgId: string, travelDistance: string[]) {
  return axios.patch(`/api/v1/insure/organisations/${orgId}/travel-distance/`, {
    travel_distance: travelDistance,
  })
}

/** Saves the freight split; tags at 0% are left out. */
export function updateFreightTypes(orgId: string, commodities: CommodityItem[]) {
  return axios.patch(`/api/v1/insure/organisations/${orgId}/freight-types/`, {
    commodities: dropZeroShares(commodities),
  })
}

export function updateLossRuns(orgId: string, formData: LossRunSummary['defaultValues']) {
  return axios.patch(`/api/v1/insure/organisations/${orgId}/loss-runs/`, formData)
}

export function uploadLossRuns(orgId: string, files: File[]) {
  const formData = new FormData()
  files.forEach((file) => formData.append('file', file))
  return axios.patch(`/api/v1/insure/organisations/${orgId}/loss-runs/`, formData)
}

export function getOrgFreightSuggestions(orgId: string): Promise<FreightSuggestions> {
  return axios
    .get(`/api/v1/insure/organisations/${orgId}/freight-types/suggestions/`)
    .then((response) => toCamelCase(response.data) as FreightSuggestions)
}

/** The freight editor's suggestions for a customer's own org; staff screens use `useFreightSuggestions`. */
export function useOrgFreightSuggestions(orgId: string | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.ORG_FREIGHT_SUGGESTIONS, orgId],
    queryFn: () => getOrgFreightSuggestions(orgId!),
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  })
  return {
    suggestions: data?.suggestions ?? [],
    suggestionGroups: data?.suggestionGroups,
    fmcsaCargo: data?.fmcsaCargo ?? [],
    isLoading,
  }
}

function getOrganisations(params?: {
  page?: number
  search?: string
  salesOwner?: string
  status?: string
  ordering?: string
}): Promise<PaginatedResults<OrgListItem>> {
  const queryParams: Record<string, string> = {}
  if (params?.page && params.page > 1) queryParams.page = String(params.page)
  if (params?.search) queryParams.search = params.search
  if (params?.salesOwner) queryParams.sales_owner = params.salesOwner
  if (params?.status) queryParams.status = params.status
  if (params?.ordering) queryParams.ordering = params.ordering
  return axios
    .get('/api/v1/insure/organisations/', { params: queryParams })
    .then((response) => toCamelCase(response.data) as PaginatedResults<OrgListItem>)
}

export function getOrganisation(id: string): Promise<Org> {
  return axios
    .get(`/api/v1/insure/organisations/${id}/`)
    .then((response) => toCamelCase(response.data) as Org)
}

/** Download this customer's data (no documents) as a fixture importable from the QA page. */
export async function exportOrg(orgId: string): Promise<void> {
  const response = await axios.get(`/api/v1/insure/organisations/${orgId}/export/`, { responseType: 'blob' })
  const disposition = String(response.headers['content-disposition'] ?? '')
  const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? `cai-org-${orgId}.json`
  const url = URL.createObjectURL(response.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function updateOrgSalesOwner(orgId: string, salesOwnerId: string | null): Promise<Org> {
  return axios
    .patch(`/api/v1/insure/organisations/${orgId}/`, { sales_owner_id: salesOwnerId })
    .then((response) => toCamelCase(response.data) as Org)
}

/** What staff set on a member of an org, adding or editing them. */
export interface OrgMemberDetails {
  firstName: string
  lastName: string
  email: string
  mobile: string
  role: OrgRole
}

function orgMemberPayload(details: OrgMemberDetails) {
  return {
    first_name: details.firstName,
    last_name: details.lastName,
    email: details.email,
    mobile: details.mobile,
    role: details.role,
  }
}

export function addOrgMember(orgId: string, details: OrgMemberDetails): Promise<void> {
  return axios
    .post(`/api/v1/insure/organisations/${orgId}/add-member/`, orgMemberPayload(details))
    .then(() => undefined)
}

export function updateOrgMember(orgId: string, userId: string, details: OrgMemberDetails): Promise<void> {
  return axios
    .patch(`/api/v1/insure/organisations/${orgId}/members/${userId}/`, orgMemberPayload(details))
    .then(() => undefined)
}

/** Switch the org the signed-in user is acting in. */
export function switchOrg(orgId: string): Promise<User> {
  return axios
    .post('/api/v1/insure/users/me/switch-org/', { org_id: orgId })
    .then((response) => toCamelCase(response.data) as User)
}

export function useSwitchOrg() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: switchOrg,
    // Every query is scoped to the active org on the backend, so nothing cached
    // for the previous org may survive the switch.
    onSuccess: () => queryClient.clear(),
  })
}

function getUserOrgs(userId: string): Promise<OrgMembership[]> {
  return axios
    .get(`/api/v1/insure/users/${userId}/organisations/`)
    .then((response) => toCamelCase(response.data) as OrgMembership[])
}

export function useUserOrgs(userId: string | undefined, enabled = true) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.USER_ORGANISATIONS, userId],
    queryFn: () => getUserOrgs(userId!),
    enabled: !!userId && enabled,
  })
  return { memberships: data ?? [], isLoading }
}

export function addUserToOrg(userId: string, orgId: string, role: OrgRole): Promise<OrgMembership[]> {
  return axios
    .post(`/api/v1/insure/users/${userId}/organisations/`, { org_id: orgId, role })
    .then((response) => toCamelCase(response.data) as OrgMembership[])
}

export function removeUserFromOrg(userId: string, orgId: string): Promise<OrgMembership[]> {
  return axios
    .delete(`/api/v1/insure/users/${userId}/organisations/${orgId}/`)
    .then((response) => toCamelCase(response.data) as OrgMembership[])
}

export function useOrganisations({
  enabled = true,
  page,
  search,
  salesOwner,
  status,
  ordering,
  keepPrevious = false,
}: {
  enabled?: boolean
  page?: number
  search?: string
  salesOwner?: string
  status?: string
  /** A CustomerSortColumn, "-" in front for descending. */
  ordering?: string
  keepPrevious?: boolean
} = {}) {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [CacheKeys.ORGANISATIONS, page, search, salesOwner, status, ordering],
    queryFn: () =>
      getOrganisations({
        page,
        search: search || undefined,
        salesOwner: salesOwner || undefined,
        status: status || undefined,
        ordering: ordering || undefined,
      }),
    enabled,
    // Off by default: a type-ahead showing the last search's rows lets you act on the wrong org.
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  })
  return {
    organisations: data?.results ?? [],
    count: data?.count ?? 0,
    hasNextPage: !!data?.next,
    hasPreviousPage: !!data?.previous,
    isLoading,
    isFetching,
  }
}

export function useOrganisation(id: string | undefined) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [CacheKeys.ORGANISATIONS, id],
    queryFn: () => getOrganisation(id!),
    enabled: !!id,
    refetchInterval: (query) =>
      query.state.data?.currentInsuranceApplication?.lossRunSummary?.isParsing ? 10_000 : false,
  })
  return { organisation: data, isLoading, refetch }
}

export function useOrgApplication(orgId: string | undefined) {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.ORGANISATIONS, orgId, 'application'],
    queryFn: async () => {
      const response = await axios.get(`/api/v1/insure/organisations/${orgId}/application/`)
      const application = toCamelCase(response.data) as InsuranceApplication
      // Same serializer as the detail endpoint: seed it so the page doesn't wait on a second full fetch.
      queryClient.setQueryData<InsuranceApplication>(
        [CacheKeys.APPLICATION, application.id],
        (current) => current ?? application
      )
      return application
    },
    enabled: !!orgId,
    retry: false,
  })
  return { application: data, isLoading }
}

export function toggleDemoMode(demoMode: boolean) {
  return axios.patch('/api/v1/insure/users/me/demo-mode/', { demo_mode: demoMode })
}

export interface CreateCustomerRequest {
  firstName: string
  lastName: string
  email: string
  phone: string
}

export interface CreateCustomerResponse {
  orgId: string
  userId: string
}

export function createCustomer(data: CreateCustomerRequest): Promise<CreateCustomerResponse> {
  return axios
    .post('/api/v1/insure/organisations/create-customer/', {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      phone: data.phone,
    })
    .then((response) => toCamelCase(response.data) as CreateCustomerResponse)
}

export function deleteCustomer(orgId: string): Promise<void> {
  return axios.delete(`/api/v1/insure/organisations/${orgId}/`).then(() => undefined)
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS] })
    },
  })
}

export interface CreateLeadFromCustomerResponse {
  leadId: string
}

function createLeadFromCustomer(orgId: string): Promise<CreateLeadFromCustomerResponse> {
  return axios
    .post(`/api/v1/insure/organisations/${orgId}/create-lead/`)
    .then((response) => toCamelCase(response.data) as CreateLeadFromCustomerResponse)
}

export function useCreateLeadFromCustomer(orgId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => createLeadFromCustomer(orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId] })
    },
  })
}

export interface SignInTokenResponse {
  token: string
}

export function getSignInToken(userId: string): Promise<SignInTokenResponse> {
  return axios
    .post(`/api/v1/insure/users/${userId}/sign-in-token/`)
    .then((response) => toCamelCase(response.data) as SignInTokenResponse)
}

export function getStaffUsers(params?: { assigned?: boolean }): Promise<StaffUser[]> {
  return axios
    .get('/api/v1/insure/users/staff/', { params: params?.assigned ? { assigned: 'true' } : undefined })
    .then((response) => toCamelCase(response.data) as StaffUser[])
}

export function useStaffUsers(params?: { assigned?: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.STAFF_USERS, params?.assigned ?? false],
    queryFn: () => getStaffUsers(params),
  })
  return { staffUsers: data ?? [], isLoading }
}

export function toggleStaffUser(userId: string, isStaff: boolean): Promise<StaffUser> {
  return axios
    .patch(`/api/v1/insure/users/${userId}/toggle-staff/`, { is_staff: isStaff })
    .then((response) => toCamelCase(response.data) as StaffUser)
}

export function toggleLeadAssignments(userId: string, receivesLeadAssignments: boolean): Promise<StaffUser> {
  return axios
    .patch(`/api/v1/insure/users/${userId}/toggle-lead-assignments/`, {
      receives_lead_assignments: receivesLeadAssignments,
    })
    .then((response) => toCamelCase(response.data) as StaffUser)
}

export interface CreateTestUserResponse {
  email: string
  firstName: string
  lastName: string
  signInToken: string
}

export function createTestUser(): Promise<CreateTestUserResponse> {
  return axios
    .post('/api/v1/insure/qa/create-test-user/')
    .then((response) => toCamelCase(response.data) as CreateTestUserResponse)
}

export function createTestAdmin(): Promise<CreateTestUserResponse> {
  return axios
    .post('/api/v1/insure/qa/create-test-admin/')
    .then((response) => toCamelCase(response.data) as CreateTestUserResponse)
}
